import React, { useState, memo, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Button,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  darken,
  TableCell,
} from "@mui/material";

import { Props, PendingApprovalsState } from "./interfaces";
import { TableContainer, ActionsCellContainer, DialogContent, Container } from "./styles";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";

import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import { StatusIndicator } from "../../../MyCalendar/DetailedFullCalendar/styles";
import { intlFormatDateMonth, intlFormatDateShort } from "../../../../utils/time-utils";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import CustomConfirmationChangesDialog from "../../../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { useTranslation } from "react-i18next";
import {
  TnAAbsencesApiService,
  approveAbsense,
  approveRejectCancellationRequest,
} from "../../../../services/tna-absences/tna-absences-api.service";
import { TnAAbsenceDTO } from "../../../../models/tna/tna-absences/tna-absence-dto";
import { useNavigate } from "react-router-dom";
import { WithEmployee } from "../../../../models/employees/state-employee";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCheck, faUserXmark, faUserClock } from "@fortawesome/free-solid-svg-icons";
import useAuthorization, { isAuthorizedEeGroup } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { TnAAbsencesFilter } from "../../../../models/tna/tna-absences/tna-absences-filter";
import { Page } from "../../../../models/api/page";
import CustomTablePagination from "../../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { LightTooltip } from "../../../AttachedDocuments/ButtonWithBadge/CustomTooltip/CustomTooltip";
import { EmployeeRef } from "../../../../models/employees";
import CommentBubble from "../../../../reusable-components/CommentBubble/CommentBubble";
import AttachedDocuments from "../../../../containers/AttachedDocuments/AttachedDocuments";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { EmployeeGroupRef } from "../../../../models/employee-groups";
import { MyTeamPendingApprovalsSummaryApiService } from "src/services/my-team-pending-approvals/my-team-pending-approvals-summary.service";
import { QuestionIcon } from "../../../../assets/icons";
import {
  ConfirmationMessageBlockContainer,
  ConfirmationMessageContainer,
  ConfirmationMessageMainContainer,
} from "../../../../containers/MyCalendar/AddEditAbsence/styles";
import { EmployeesTnACountersFilter } from "../../../../models/pending-approvals/employees-tna-counters.filter";
import { EmployeesTnACountersApiService } from "../../../../services/employees-tna-counters/employees-tna-counters.service";
import { getCountersPerEmployee, getOverlappingAbsences, getYearsCountersForEmployees } from "./utils";
import { OverlappingAbsencesApiService } from "../../../../services/tna-overlapping-absences/tna-overlapping-absences-api.service";
import OverlappingAbsences from "./OverlappingAbsences/OverlappingAbsences";
import { FT_MTPAL } from "../../../../utils/constants/filter-tables-constants";

const PendingApprovals = ({ employee, showEe, height = 400 }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";
  const theme = useTheme();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const hasAccessThroughAuth = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);
  const hasAccess = employee
    ? isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
      )
    : hasAccessThroughAuth;

  const viewOnly = isAuthorizedEeGroup(
    [AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData],
    currentUserAuthorizations,
    employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
  );

  const navigate = useNavigate();

  const navigateToTnA = (employee: EmployeeRef | null) => {
    hasAccess &&
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        } as WithEmployee,
      });
  };

  const navigateToVacationTracking = (employee: EmployeeRef | null) => {
    if (hasAccess) {
      localStorage.setItem(
        `${FT_MTPAL}`,
        JSON.stringify({
          query: employee?.name ?? "",
          exactMatch: false,
        })
      );

      navigate("/my-team-presence-and-leaves?tab=vacation-tracking");
    }
  };

  const [state, setState] = useState<PendingApprovalsState>({
    loading: false,
    error: "",
    items: [],
    countersPerEmployee: {},
    overlappingAbsences: {},
    isMutated: false,
    mutatingStatus: "",
    confirmStatus: false,
    approveRejectRequestCancellation: "",
    mutatingStatusValue: false,
    isConfirmationDialogOpen: false,
    confirmationDialogTitle: "",
    confirmationDialogMessage: <></>,
    confirmationDialogActionsAndHandlers: [],
    absence: null,
    totalItems: 0,
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
    summary: null,
  });

  const handleSetPage = (field: keyof Page, value: Page[keyof Page]): void =>
    setState((state) => ({
      ...state,
      page: { ...state.page, [field]: value },
    }));

  const loadAbsences = useCallback(async (id: string | null, page: Page) => {
    const response = await new TnAAbsencesApiService().loadItems(
      {
        employeeIds: id ? [id] : [],
        pending: true,
        cancellationRequestPending: true,
        includeSignedOff: true,
      } as TnAAbsencesFilter,
      undefined,
      undefined,
      page,
      undefined,
      60
    );

    return response;
  }, []);

  const loadCounters = useCallback(async (absences: TnAAbsenceDTO[]) => {
    const employeesAndYears = getYearsCountersForEmployees(absences);

    const response = await new EmployeesTnACountersApiService().loadItems(
      {
        employeesAndYears: employeesAndYears,
      } as EmployeesTnACountersFilter,
      undefined,
      undefined,
      { pageNumber: 0, pageSize: 10000 },
      undefined,
      60
    );

    return response;
  }, []);

  const loadOverlappingAbsences = useCallback(async (absences: TnAAbsenceDTO[]) => {
    const absencesIds = absences.map((abs) => abs!.id);

    const response = await new OverlappingAbsencesApiService().loadItems(
      {
        ids: absencesIds,
      } as TnAAbsencesFilter,
      undefined,
      undefined,
      { pageNumber: 0, pageSize: 10000 },
      undefined,
      60
    );

    return response;
  }, []);

  const loadData = useCallback(
    async (id: string | null, signal?: AbortSignal) => {
      setState((state) => ({
        ...state,
        loading: true,
      }));

      try {
        const absencesRes = await loadAbsences(id, state.page);

        if (absencesRes.error) {
          setState((state) => ({
            ...state,
            items: [],
            totalItems: 0,
            loading: false,
            error: absencesRes.error,
            isMutated: false,
            countersPerEmployee: {},
            overlappingAbsences: {},
          }));

          toastifyResponse(absencesRes, "failed", undefined, "failure_fetching_data", "error_fetching_data");
        } else {
          if (!!absencesRes.items.length) {
            const countersRes = await loadCounters(absencesRes.items);

            if (countersRes.error) {
              setState((state) => ({
                ...state,
                items: [],
                totalItems: 0,
                loading: false,
                error: absencesRes.error,
                isMutated: false,
                countersPerEmployee: {},
                overlappingAbsences: {},
              }));

              toastifyResponse(countersRes, "failed", undefined, "failure_fetching_data", "error_fetching_data");
            } else {
              const overlappingAbsencesRes = await loadOverlappingAbsences(absencesRes.items);

              if (overlappingAbsencesRes.error) {
                setState((state) => ({
                  ...state,
                  items: [],
                  totalItems: 0,
                  loading: false,
                  error: overlappingAbsencesRes.error,
                  isMutated: false,
                  countersPerEmployee: {},
                  overlappingAbsences: {},
                }));

                toastifyResponse(
                  overlappingAbsencesRes,
                  "failed",
                  undefined,
                  "failure_fetching_data",
                  "error_fetching_data"
                );
              } else {
                setState((state) => ({
                  ...state,
                  items: absencesRes.items,
                  totalItems: absencesRes.totalItems,
                  countersPerEmployee: getCountersPerEmployee(countersRes.items),
                  overlappingAbsences: getOverlappingAbsences(overlappingAbsencesRes.items),
                  loading: false,
                  error: "",
                  isMutated: false,
                }));
              }
            }
          } else {
            setState((state) => ({
              ...state,
              items: absencesRes.items,
              totalItems: absencesRes.totalItems,
              loading: false,
              error: "",
              isMutated: false,
              countersPerEmployee: {},
              overlappingAbsences: {},
            }));
          }
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          loading: false,
          error: error as string,
          isMutated: false,
        }));
      }
    },
    [state.page]
  );

  const handleSaveStatus = useCallback(async () => {
    if (state.confirmStatus && state.absence && hasAccess) {
      setState((state) => ({
        ...state,
        loading: true,
      }));
      try {
        const isApproved =
          state.mutatingStatus === "isRejected" && !state.mutatingStatusValue
            ? false
            : state.mutatingStatus === "isApproved"
            ? !state.mutatingStatusValue
            : state.absence.isApproved;

        const isRejected =
          state.mutatingStatus === "isApproved" && !state.mutatingStatusValue
            ? false
            : state.mutatingStatus === "isRejected"
            ? !state.mutatingStatusValue
            : state.absence.isRejected;

        const apiService = new TnAAbsencesApiService();
        apiService.useSaveItemsPath(`/CancellationRequestTnAAbsences`);

        const { error } = !!state.approveRejectRequestCancellation
          ? await approveRejectCancellationRequest(
              state.absence.employee?.id as string,
              state.absence.id,
              state.approveRejectRequestCancellation === "approve" ? "isApproved" : "isRejected"
            )
          : await approveAbsense(
              state.absence.employee?.id as string,
              state.absence.id,
              isApproved ? "isApproved" : "isRejected"
            );

        if (!error) {
          setState((state) => ({
            ...state,
            mutatingStatus: "",
            confirmStatus: false,
            mutatingStatusValue: false,
            isConfirmationDialogOpen: false,
            approveRejectRequestCancellation: "",
            confirmationDialogTitle: "",
            confirmationDialogMessage: <></>,
            confirmationDialogActionsAndHandlers: [],
            absence: null,
            isMutated: true,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          mutatingStatus: "",
          confirmStatus: false,
          mutatingStatusValue: false,
          isConfirmationDialogOpen: false,
          approveRejectRequestCancellation: "",
          confirmationDialogTitle: "",
          confirmationDialogMessage: <></>,
          confirmationDialogActionsAndHandlers: [],
          absence: null,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }
  }, [
    state.confirmStatus,
    state.mutatingStatus,
    state.mutatingStatusValue,
    state.absence,
    hasAccess,
    state.approveRejectRequestCancellation,
  ]);

  const handleConfirmationCallback = useCallback((approveRejectRequestCancellation?: "approve" | "reject") => {
    setState((state) => ({
      ...state,
      confirmStatus: true,
      approveRejectRequestCancellation: approveRejectRequestCancellation ?? "",
    }));
  }, []);

  const handleCancelConfirmationDialog = () => {
    setState((state) => ({
      ...state,
      isConfirmationDialogOpen: false,
      confirmationDialogTitle: "",
      approveRejectRequestCancellation: "",
      confirmationDialogMessage: <></>,
      confirmationDialogActionsAndHandlers: [],
    }));
  };

  const loadSummary = useCallback(async () => {
    try {
      const response = await new MyTeamPendingApprovalsSummaryApiService().loadItems({}, "", undefined, {
        pageNumber: 0,
        pageSize: 10000,
      });
      if (response.error) {
        console.log(response.error);
      } else {
        setState((state) => ({ ...state, summary: response.items[0] }));
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    state.confirmStatus && handleSaveStatus();
  }, [state.confirmStatus, handleSaveStatus]);

  useEffect(() => {
    const abortController = new AbortController();
    if (employee?.id || state.isMutated) {
      loadData(employee?.id ?? null, abortController.signal);
    } else {
      loadData(null, abortController.signal);
    }
    loadSummary();
    return () => abortController.abort();
  }, [employee?.id, state.isMutated, state.page, loadSummary]);

  const columns = [
    {
      title: t(`common_words.employee`),
      field: "employee",
    },
    {
      title: t(`${tRP}.fields.from_date`),
      field: "fromDate",
    },
    {
      title: t(`${tRP}.fields.to_date`),
      field: "toDate",
    },
    {
      title: t(`common_words.document`),
      field: "documentation",
    },
    {
      title: `${t(`${tRP}.fields.absence`)}s`,
      field: "absences",
    },
    {
      title: `${t(`${tRP}.vacation_balance`)}`,
      field: "counters",
    },
    {
      title: `${t(`${tRP}.overlapping_absences`)}`,
      field: "overlapping_absences",
    },
    {
      title: `${t(`${tRP}.fields.actions`)}`,
      field: "actions",
    },
  ];

  return (
    <>
      {(hasAccess || viewOnly) && (
        <>
          <LoadingOverlay loading={state.loading}>
            <DialogContent>
              {state.items?.length === 0 && !state.loading ? (
                <Grid
                  sx={{
                    width: "100%",
                    height: `${height}px`,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Alert severity="info">
                    {state.summary
                      ? state.summary.expenseClaims > 0
                        ? state.summary.pendingExtraHours > 0
                          ? `You have no pending absence requests but you have ${state.summary.expenseClaims} pending expense report and ${state.summary.pendingExtraHours} pending extra hours request`
                          : `You have no pending absence requests but you have ${state.summary.expenseClaims} pending expense reports`
                        : state.summary.pendingExtraHours > 0
                        ? `You have no pending absence requests but you have ${state.summary.pendingExtraHours} pending extra hours request`
                        : "No pending approvals"
                      : "No pending approvals"}
                    {/* <Typography>No items</Typography> */}
                  </Alert>
                </Grid>
              ) : (
                <>
                  <Container
                    style={{
                      width: "100%",
                      height: `${height}px`,
                    }}
                  >
                    <TableContainer>
                      <Table
                        size={"small"}
                        stickyHeader={true}
                      >
                        <TableHead>
                          <TableRow>
                            {columns.map((column, idx) => {
                              if (!showEe && column.field === "employee") return <></>;

                              return (
                                <TableCell
                                  key={idx}
                                  align="center"
                                  sx={{
                                    ...(column.field === "overlapping_absences" && {
                                      width: "100px",
                                    }),
                                    ...(column.field === "counters" && {
                                      minWidth: "190px",
                                    }),
                                  }}
                                >
                                  <Typography>{column.title}</Typography>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {state.items.map((item, idx) => {
                            const employeeCounters = item?.employee?.id
                              ? state.countersPerEmployee[item.employee.id]?.filter(
                                  (counter) =>
                                    counter.year === item.fromDate?.year || counter.year === item.toDate?.year
                                ) ?? []
                              : [];

                            const overlappingAbsences = state.overlappingAbsences[item.id] ?? null;

                            return (
                              <TableRow key={idx}>
                                {showEe && (
                                  <TableCell align="left">
                                    <Grid
                                      sx={{
                                        padding: "5px",
                                        "&:hover, &:hover .MuiTypography-root": {
                                          cursor: "pointer",
                                        },
                                      }}
                                    >
                                      <Grid
                                        onClick={() => {
                                          navigateToTnA(item.employee);
                                        }}
                                        title={"Go to employee timesheet"}
                                      >
                                        {item?.employee?.name && (
                                          <Typography sx={{ fontWeight: "bold", textAlign: "left" }}>
                                            {item.employee.name}
                                          </Typography>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </TableCell>
                                )}

                                <TableCell align="center">
                                  {item.fromDate?.year && item.fromDate?.month && item.fromDate?.day && (
                                    <Typography>
                                      {intlFormatDateShort(
                                        new Date(item.fromDate.year, item.fromDate.month - 1, item.fromDate.day)
                                      )}
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell align="center">
                                  {item.toDate?.year && item.toDate?.month && item.toDate?.day && (
                                    <Typography>
                                      {intlFormatDateShort(
                                        new Date(item.toDate.year, item.toDate.month - 1, item.toDate.day)
                                      )}
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell align="center">
                                  <Grid
                                    sx={{
                                      display: "flex",
                                      width: "100%",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    {item?.documentation && (
                                      <AttachedDocuments
                                        count={item.documentation?.filesCount}
                                        documentIDs={[item.documentation?.id]}
                                        buttonSize={"2x"}
                                        documentsReadOnlyFields={[
                                          {
                                            documentID: item?.documentation?.id,
                                            readOnlyFields: {
                                              isEmployeeRO: true,
                                              isCategoryRO: true,
                                              isLegalEntityRO: true,
                                              isTypeRO: true,
                                              isDateRO: true,
                                              isStatusRO: true,
                                            },
                                          },
                                        ]}
                                        hideReadOnlyFields={true}
                                      />
                                    )}
                                  </Grid>
                                </TableCell>

                                <TableCell align="center">
                                  <Grid sx={{ position: "relative" }}>
                                    <StatusIndicator
                                      isRejected={item.isRejected}
                                      isCancelled={item.isCancelled}
                                      isApproved={item.isApproved}
                                      key={item.id}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "row",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          gap: "5px",
                                          position: "relative",
                                        }}
                                      >
                                        <LightTooltip
                                          title={
                                            <Grid sx={{ display: "flex", flexDirection: "column", maxWidth: "300px" }}>
                                              <Typography>
                                                {item.absenceType?.name +
                                                  ": " +
                                                  (item.fromDate?.year !== item.toDate?.year ||
                                                  item.fromDate?.month !== item.toDate?.month ||
                                                  item.fromDate?.day !== item.toDate?.day
                                                    ? `${intlFormatDateMonth(
                                                        CustomDate.ToDate(item.fromDate) as Date
                                                      )} - ${intlFormatDateMonth(
                                                        CustomDate.ToDate(item.toDate) as Date
                                                      )}`
                                                    : `${intlFormatDateMonth(
                                                        CustomDate.ToDate(item.fromDate) as Date
                                                      )}`)}
                                              </Typography>

                                              <Typography>
                                                Created by:{" "}
                                                <b>{item.createdByUser?.name ?? item.createdByUser?.email ?? ""}</b>
                                              </Typography>

                                              {item.modifiedByUser &&
                                                (item.isApproved || item.isRejected) &&
                                                !item.isCancelled && (
                                                  <>
                                                    {item.isApproved && (
                                                      <Typography>
                                                        Approved by: <b>{item.modifiedByUser?.name ?? ""}</b>
                                                      </Typography>
                                                    )}

                                                    {item.isRejected && (
                                                      <Typography>
                                                        Rejected by: <b>{item.modifiedByUser?.name ?? ""}</b>
                                                      </Typography>
                                                    )}
                                                  </>
                                                )}
                                            </Grid>
                                          }
                                        >
                                          <Typography>
                                            {`${item.absenceType?.code}${
                                              !item.isRejected && !item.isCancelled && !item.isApproved ? " ?" : ""
                                            }`}
                                          </Typography>
                                        </LightTooltip>

                                        {!!item.countedDurationDays && (
                                          <Typography
                                            variant="body2"
                                            title={`Counted days :  ${item.countedDurationDays} day${
                                              item.countedDurationDays > 1 ? "s" : ""
                                            }/ Total absence days : ${item.calendaristicDays} day${
                                              item.calendaristicDays > 1 ? "s" : ""
                                            }`}
                                          >{` ${item.countedDurationDays} day${
                                            item.countedDurationDays > 1 ? "s" : ""
                                          } `}</Typography>
                                        )}

                                        <React.Fragment>
                                          {!!item.cancelRequested &&
                                            !!item.cancelRequestedAt &&
                                            item.cancellationRequestApproved === null && (
                                              <QuestionIcon
                                                style={{
                                                  width: "15px",
                                                  height: "15px",
                                                  position: "absolute",
                                                  right: "-8px",
                                                  top: "-8px",
                                                }}
                                                title={`Requested absence cancellation on ${intlFormatDateShort(
                                                  new Date(item.cancelRequestedAt)
                                                )}`}
                                              />
                                            )}
                                          {item.needAcceptance &&
                                            (item.acceptedByClient ? (
                                              <FontAwesomeIcon
                                                icon={faUserCheck}
                                                color={"greenyellow"}
                                                title={`Accepted by client ${
                                                  item.acceptedRejectedByUser?.name ?? item.acceptedRejectedByEmail
                                                }`}
                                              />
                                            ) : item.rejectedByClient ? (
                                              <FontAwesomeIcon
                                                icon={faUserXmark}
                                                color={theme.palette.error.main}
                                                title={`Rejected by client ${
                                                  item.acceptedRejectedByUser?.name ?? item.acceptedRejectedByEmail
                                                }`}
                                              />
                                            ) : (
                                              <FontAwesomeIcon
                                                icon={faUserClock}
                                                color={theme.palette.text.secondary}
                                                title={"Pending client acceptance"}
                                              />
                                            ))}
                                        </React.Fragment>
                                      </div>
                                    </StatusIndicator>

                                    {item?.comment && (
                                      <Grid sx={{ position: "absolute", right: "-15px", top: "-10px" }}>
                                        <CommentBubble
                                          title={`${t(`${tRP}.fields.comment`)}:`}
                                          comment={item.comment}
                                          customButtonStyles={{
                                            width: "18px",
                                            height: "18px",
                                            color: theme.palette.primary.main,
                                          }}
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                </TableCell>

                                <TableCell>
                                  <Grid
                                    onClick={() => navigateToVacationTracking(item.employee)}
                                    sx={{
                                      width: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "5px",
                                      "&:hover p": {
                                        cursor: "pointer",
                                      },
                                    }}
                                    title={"Click to see team vacation tracking screen"}
                                  >
                                    {employeeCounters.map((counter) => {
                                      return (
                                        <React.Fragment key={counter?.id}>
                                          <Grid
                                            sx={{
                                              width: "100%",
                                              display: "flex",
                                              flexDirection: "column",
                                            }}
                                          >
                                            <Typography sx={{ fontWeight: "bold" }}>In {counter.year}</Typography>
                                            <Typography>Vacation balance: {counter.balance.toFixed(2)}</Typography>
                                            <Typography>Used/Planned: {counter.usedOrPlanned.toFixed(2)}</Typography>
                                          </Grid>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Grid>
                                </TableCell>

                                <TableCell>
                                  <Grid sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                    {overlappingAbsences && (
                                      <OverlappingAbsences absences={overlappingAbsences.overlappingAbsences} />
                                    )}
                                  </Grid>
                                </TableCell>

                                <TableCell align="center">
                                  {!(item.cancelRequested && item.cancellationRequestApproved === null) && (
                                    <ActionsCellContainer>
                                      {[
                                        {
                                          action: "reject",
                                          field: "isRejected",
                                          stateField: item.isRejected,
                                        },
                                        {
                                          action: "approve",
                                          field: "isApproved",
                                          stateField: item.isApproved,
                                        },
                                      ].map(({ action, field, stateField }, idx) => (
                                        <Button
                                          key={idx}
                                          color={field === "isApproved" ? "success" : "warning"}
                                          variant={"contained"}
                                          disabled={stateField || (viewOnly && !hasAccess)}
                                          onClick={() => {
                                            setState((state) => ({
                                              ...state,
                                              absence: item as TnAAbsenceDTO,
                                              mutatingStatus: field,
                                              mutatingStatusValue: stateField,
                                              isConfirmationDialogOpen: true,
                                              confirmationDialogTitle: t(`${tRP}.confirmation_dialog.titles.${action}`),
                                              confirmationDialogMessage: (
                                                <Typography>{`${t(
                                                  `${tRP}.confirmation_dialog.messages.${action}`
                                                )}`}</Typography>
                                              ),
                                              confirmationDialogActionsAndHandlers: [
                                                {
                                                  action: t(`common_words.yes`),
                                                  buttonVariant: "contained",
                                                  handler: handleConfirmationCallback,
                                                },
                                              ],
                                            }));
                                          }}
                                        >
                                          {t(`${tRP}.status_buttons.${action}`)}
                                        </Button>
                                      ))}
                                    </ActionsCellContainer>
                                  )}

                                  {item.cancelRequested && item.cancellationRequestApproved === null && (
                                    <ActionsCellContainer>
                                      <Button
                                        sx={{
                                          background: theme.palette.warning.light,

                                          "&:hover": {
                                            background: darken(theme.palette.warning.light, 0.15),
                                          },
                                        }}
                                        variant={"contained"}
                                        onClick={() => {
                                          setState((state) => ({
                                            ...state,
                                            absence: item as TnAAbsenceDTO,
                                            isConfirmationDialogOpen: true,
                                            confirmationDialogTitle: t(
                                              `${tRP}.confirmation_dialog.titles.approve_absence_cancellation`
                                            ),
                                            confirmationDialogMessage: (
                                              <ConfirmationMessageMainContainer>
                                                <Typography>
                                                  You are about to cancel the following absence request:
                                                </Typography>
                                                <ConfirmationMessageContainer>
                                                  <ConfirmationMessageBlockContainer>
                                                    <Typography sx={{ fontWeight: "bold" }}>{`Employee:`}</Typography>
                                                    <Typography>{`${item.employee?.name ?? ""}`}</Typography>
                                                  </ConfirmationMessageBlockContainer>
                                                  <ConfirmationMessageBlockContainer>
                                                    <Typography sx={{ fontWeight: "bold" }}>{`From`}</Typography>
                                                    <Typography>{`${
                                                      !!item?.fromDate
                                                        ? intlFormatDateShort(
                                                            new Date(
                                                              item.fromDate.year,
                                                              item.fromDate.month - 1,
                                                              item.fromDate.day
                                                            )
                                                          )
                                                        : ""
                                                    }`}</Typography>
                                                    <Typography sx={{ fontWeight: "bold" }}>{`to`}</Typography>
                                                    <Typography>{`${
                                                      !!item?.toDate
                                                        ? intlFormatDateShort(
                                                            new Date(
                                                              item.toDate.year,
                                                              item.toDate.month - 1,
                                                              item.toDate.day
                                                            )
                                                          )
                                                        : ""
                                                    }`}</Typography>
                                                  </ConfirmationMessageBlockContainer>
                                                </ConfirmationMessageContainer>
                                                <Typography>
                                                  Cancellation of approved absences may be restricted by rules or
                                                  policies. Are you sure you want to proceed?
                                                </Typography>
                                              </ConfirmationMessageMainContainer>
                                            ),
                                            confirmationDialogActionsAndHandlers: [
                                              {
                                                action: t(`${tRP}.status_buttons.accept_absence_cancelation`),
                                                buttonVariant: "contained",
                                                handler: () => handleConfirmationCallback("approve"),
                                              },
                                            ],
                                          }));
                                        }}
                                      >
                                        {t(`${tRP}.status_buttons.approve_cancellation`)}
                                      </Button>

                                      <Button
                                        sx={{
                                          background: theme.palette.error.light,

                                          "&:hover": {
                                            background: darken(theme.palette.error.light, 0.15),
                                          },
                                        }}
                                        variant={"contained"}
                                        onClick={() => {
                                          setState((state) => ({
                                            ...state,
                                            absence: item as TnAAbsenceDTO,
                                            isConfirmationDialogOpen: true,
                                            confirmationDialogTitle: t(
                                              `${tRP}.confirmation_dialog.titles.reject_absence_cancellation`
                                            ),
                                            confirmationDialogMessage: (
                                              <>
                                                <ConfirmationMessageMainContainer>
                                                  <Typography>
                                                    You are about to reject the cancellation request of the following
                                                    absence:
                                                  </Typography>
                                                  <ConfirmationMessageContainer>
                                                    <ConfirmationMessageBlockContainer>
                                                      <Typography sx={{ fontWeight: "bold" }}>{`Employee:`}</Typography>
                                                      <Typography>{`${item.employee?.name ?? ""}`}</Typography>
                                                    </ConfirmationMessageBlockContainer>
                                                    <ConfirmationMessageBlockContainer>
                                                      <Typography sx={{ fontWeight: "bold" }}>{`From`}</Typography>
                                                      <Typography>{`${
                                                        !!item?.fromDate
                                                          ? intlFormatDateShort(
                                                              new Date(
                                                                item.fromDate.year,
                                                                item.fromDate.month - 1,
                                                                item.fromDate.day
                                                              )
                                                            )
                                                          : ""
                                                      }`}</Typography>
                                                      <Typography sx={{ fontWeight: "bold" }}>{`to`}</Typography>
                                                      <Typography>{`${
                                                        !!item?.toDate
                                                          ? intlFormatDateShort(
                                                              new Date(
                                                                item.toDate.year,
                                                                item.toDate.month - 1,
                                                                item.toDate.day
                                                              )
                                                            )
                                                          : ""
                                                      }`}</Typography>
                                                    </ConfirmationMessageBlockContainer>
                                                  </ConfirmationMessageContainer>
                                                  <Typography>Are you sure you want to proceed?</Typography>
                                                </ConfirmationMessageMainContainer>
                                              </>
                                            ),
                                            confirmationDialogActionsAndHandlers: [
                                              {
                                                action: t(`${tRP}.status_buttons.reject_request_to_cancel_absence`),
                                                buttonVariant: "contained",
                                                handler: () => handleConfirmationCallback("reject"),
                                              },
                                            ],
                                          }));
                                        }}
                                      >
                                        {t(`${tRP}.status_buttons.reject_cancellation`)}
                                      </Button>
                                    </ActionsCellContainer>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Container>
                </>
              )}
            </DialogContent>

            <CustomTablePagination
              totalItems={state.totalItems}
              pageNumber={state.page.pageNumber}
              handleChangePageNumber={(p) => handleSetPage("pageNumber", p)}
              pageSize={state.page.pageSize}
              handleChangePageSize={(s) => {
                handleSetPage("pageSize", s);
                handleSetPage("pageNumber", 0);
              }}
            />
          </LoadingOverlay>
        </>
      )}
      {
        <CustomConfirmationChangesDialog
          open={state.isConfirmationDialogOpen}
          loading={state.loading}
          title={state.confirmationDialogTitle}
          children={state.confirmationDialogMessage}
          handleCancel={handleCancelConfirmationDialog}
          actionsAndHandlers={state.confirmationDialogActionsAndHandlers}
        />
      }
    </>
  );
};

export default memo(PendingApprovals);
